/**
 * Pipeline stage definitions.
 *
 * The 18 stages of the procurement pipeline, with their metadata.
 * This is the single source of truth — referenced by the API gate logic,
 * the UI's pipeline visualization, and the audit log.
 */

import type { ItemType } from './enums.js';

// ---------------------------------------------------------------------------
// Stage groups (used for visual clustering in the pipeline view)
// ---------------------------------------------------------------------------

export const StageGroups = [
  'PROCUREMENT', // stages 1-5 (per batch)
  'PREPARATION', // stages 6-8
  'INSPECTION', // stages 9-11
  'DELIVERY', // stages 12-13
  'BILLING', // stages 14-18
] as const;
export type StageGroup = (typeof StageGroups)[number];

// ---------------------------------------------------------------------------
// Stage ownership — does the stage live on a batch or on a part?
// ---------------------------------------------------------------------------

export const StageOwnerships = ['ORDER_BATCH', 'PART'] as const;
export type StageOwnership = (typeof StageOwnerships)[number];

// ---------------------------------------------------------------------------
// One definition per stage
// ---------------------------------------------------------------------------

export interface StageDefinition {
  /** 1..18 */
  number: number;
  /** Stable machine key */
  key: string;
  /** Display label */
  label: string;
  /** Which group it visually clusters into */
  group: StageGroup;
  /** Whether the stage lives on an order batch (1-5) or on a part (6-18) */
  ownership: StageOwnership;
  /** Whether this stage records an inspection event (date + outcome + notes + resolved date) */
  isOutcomeBearing: boolean;
  /** Whether the stage is single-instance per entity (true) or per-batch (false) */
  isSingleInstance: boolean;
  /**
   * When true, this stage can be auto-skipped (marked N/A) based on a prior stage's outcome.
   * Only stages 10 and 11 — skipped when stage 9 outcome is ACCEPTED.
   */
  canBeSkipped: boolean;
}

export const PIPELINE_STAGES: readonly StageDefinition[] = [
  // PROCUREMENT — per order batch
  {
    number: 1,
    key: 'ORDER_PLACED',
    label: 'Order Placed',
    group: 'PROCUREMENT',
    ownership: 'ORDER_BATCH',
    isOutcomeBearing: false,
    isSingleInstance: false,
    canBeSkipped: false,
  },
  {
    number: 2,
    key: 'PRE_SHIPMENT_INSPECTION',
    label: 'Pre-shipment Inspection',
    group: 'PROCUREMENT',
    ownership: 'ORDER_BATCH',
    isOutcomeBearing: false,
    isSingleInstance: false,
    canBeSkipped: false,
  },
  {
    number: 3,
    key: 'IN_TRANSIT',
    label: 'In Transit',
    group: 'PROCUREMENT',
    ownership: 'ORDER_BATCH',
    isOutcomeBearing: false,
    isSingleInstance: false,
    canBeSkipped: false,
  },
  {
    number: 4,
    key: 'ARRIVED',
    label: 'Arrived',
    group: 'PROCUREMENT',
    ownership: 'ORDER_BATCH',
    isOutcomeBearing: false,
    isSingleInstance: false,
    canBeSkipped: false,
  },
  {
    number: 5,
    key: 'IN_HOUSE_INSPECTION',
    label: 'In-house Inspection',
    group: 'PROCUREMENT',
    ownership: 'ORDER_BATCH',
    isOutcomeBearing: false,
    isSingleInstance: false,
    canBeSkipped: false,
  },
  // PREPARATION — per part
  {
    number: 6,
    key: 'ASSEMBLING',
    label: 'Assembling',
    group: 'PREPARATION',
    ownership: 'PART',
    isOutcomeBearing: false,
    isSingleInstance: true,
    canBeSkipped: false,
  },
  {
    number: 7,
    key: 'PACKING_LABELLING',
    label: 'Packing & Labelling',
    group: 'PREPARATION',
    ownership: 'PART',
    isOutcomeBearing: false,
    isSingleInstance: true,
    canBeSkipped: false,
  },
  {
    number: 8,
    key: 'INSPECTION_OFFERED',
    label: 'Inspection offered',
    group: 'PREPARATION',
    ownership: 'PART',
    isOutcomeBearing: false,
    isSingleInstance: true,
    canBeSkipped: false,
  },
  // INSPECTION — per part, outcome-bearing
  {
    number: 9,
    key: 'INSPECTION',
    label: 'Inspection',
    group: 'INSPECTION',
    ownership: 'PART',
    isOutcomeBearing: true,
    isSingleInstance: true,
    canBeSkipped: false,
  },
  {
    number: 10,
    key: 'RE_INSPECTION_OFFERED',
    label: 'Re-inspection Offered',
    group: 'INSPECTION',
    ownership: 'PART',
    isOutcomeBearing: false,
    isSingleInstance: true,
    canBeSkipped: true,
  },
  {
    number: 11,
    key: 'RE_INSPECTION',
    label: 'Re-inspection',
    group: 'INSPECTION',
    ownership: 'PART',
    isOutcomeBearing: true,
    isSingleInstance: true,
    canBeSkipped: true,
  },
  // DELIVERY — per part
  {
    number: 12,
    key: 'DELIVERED',
    label: 'Delivered',
    group: 'DELIVERY',
    ownership: 'PART',
    isOutcomeBearing: false,
    isSingleInstance: true,
    canBeSkipped: false,
  },
  {
    number: 13,
    key: 'FUNCTIONAL_CHECK',
    label: 'Functional Check',
    group: 'DELIVERY',
    ownership: 'PART',
    isOutcomeBearing: true,
    isSingleInstance: true,
    canBeSkipped: false,
  },
  // BILLING — per part
  {
    number: 14,
    key: 'I_NOTE_RECEIVED',
    label: 'I-Note Received',
    group: 'BILLING',
    ownership: 'PART',
    isOutcomeBearing: false,
    isSingleInstance: true,
    canBeSkipped: false,
  },
  {
    number: 15,
    key: 'CRV_ISSUED',
    label: 'CRV Issued',
    group: 'BILLING',
    ownership: 'PART',
    isOutcomeBearing: false,
    isSingleInstance: true,
    canBeSkipped: false,
  },
  {
    number: 16,
    key: 'INVOICE_SUBMITTED',
    label: 'Invoice Submitted',
    group: 'BILLING',
    ownership: 'PART',
    isOutcomeBearing: false,
    isSingleInstance: true,
    canBeSkipped: false,
  },
  {
    number: 17,
    key: 'CHEQUE_RELEASED',
    label: 'Cheque Released',
    group: 'BILLING',
    ownership: 'PART',
    isOutcomeBearing: false,
    isSingleInstance: true,
    canBeSkipped: false,
  },
  {
    number: 18,
    key: 'CHEQUE_CLEARED',
    label: 'Cheque Cleared',
    group: 'BILLING',
    ownership: 'PART',
    isOutcomeBearing: false,
    isSingleInstance: true,
    canBeSkipped: false,
  },
] as const;

// ---------------------------------------------------------------------------
// Derived helpers and constants
// ---------------------------------------------------------------------------

export const STAGE_BY_NUMBER: Readonly<Record<number, StageDefinition>> = Object.freeze(
  PIPELINE_STAGES.reduce<Record<number, StageDefinition>>((acc, stage) => {
    acc[stage.number] = stage;
    return acc;
  }, {}),
);

export const STAGE_BY_KEY: Readonly<Record<string, StageDefinition>> = Object.freeze(
  PIPELINE_STAGES.reduce<Record<string, StageDefinition>>((acc, stage) => {
    acc[stage.key] = stage;
    return acc;
  }, {}),
);

/** Stage 16 has a special cross-project dependency: it requires the project to have an accepted BG. */
export const INVOICE_STAGE_NUMBER = 16;

/** Stages that block all subsequent stages until completed — the aggregate gate. */
export const AGGREGATE_GATE_STAGE = 6;

/** Stage numbers that record inspection events (date + outcome + resolution). */
export const OUTCOME_BEARING_STAGE_NUMBERS: readonly number[] = PIPELINE_STAGES.filter(
  (s) => s.isOutcomeBearing,
).map((s) => s.number);

/** Stages that apply only to PART entities (not sub-parts). */
export const PART_ONLY_STAGE_NUMBERS: readonly number[] = PIPELINE_STAGES.filter(
  (s) => s.ownership === 'PART',
).map((s) => s.number);

/** Stages that apply only to order batches. */
export const ORDER_BATCH_STAGE_NUMBERS: readonly number[] = PIPELINE_STAGES.filter(
  (s) => s.ownership === 'ORDER_BATCH',
).map((s) => s.number);

/**
 * Stage numbers that apply to a given entity type.
 *
 * - PART: stages 1–18 if no sub-parts; stages 6–18 if it has sub-parts.
 *   (The latter is decided in business logic at read time; this helper returns the
 *    maximal set, and the caller filters out 1–5 for Variant A parts.)
 * - SUB_PART: stages 1–5 only.
 */
export function applicableStageNumbersFor(itemType: ItemType): readonly number[] {
  if (itemType === 'SUB_PART') return ORDER_BATCH_STAGE_NUMBERS;
  return PIPELINE_STAGES.map((s) => s.number);
}
